import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAlertSound } from './useAlertSound';
import { useAudioLevel } from './useAudioLevel';

export type AudioAlertType = 'SILENT' | 'VERY_LOW' | null;

export interface AudioLevelAlertConfig {
  disabled: boolean;
  initialSilenceThresholdMs: number;
  silenceThresholdMs: number;
  alertSoundCooldownMs: number;
}

const DEFAULT_CONFIG: AudioLevelAlertConfig = {
  disabled: false,
  initialSilenceThresholdMs: 20000, // 20 seconds
  // Post-speech threshold: once the user has spoken, alert after 45s of silence
  // (a muted/disconnected mic). Callers can override (e.g. the dictation bubble).
  silenceThresholdMs: 45000, // 45 seconds
  alertSoundCooldownMs: 60000, // 1 minute
};

export const useGenericAudioLevelAlert = (
  isWatching: boolean,
  useSound: boolean,
  onGoodAudioSeen: () => void,
  config: Partial<AudioLevelAlertConfig> = {}
) => {
  const finalConfig = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config]);
  const [alertType, setAlertType] = useState<AudioAlertType>(null);
  const [hasHadGoodAudio, setHasHadGoodAudio] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const currentAudioLevel = useAudioLevel();
  const { playAlert } = useAlertSound(finalConfig.alertSoundCooldownMs);

  const updateAlertType = useCallback(
    (newAlertType: AudioAlertType) => {
      if (finalConfig.disabled) return;
      if (useSound && alertType === null && newAlertType === 'SILENT') playAlert();
      setAlertType(newAlertType);
    },
    [useSound, alertType, playAlert, finalConfig]
  );

  const clearTimeout = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetAlertState = useCallback(() => {
    updateAlertType(null);
    clearTimeout();
  }, [clearTimeout, updateAlertType]);

  const reset = useCallback(() => {
    resetAlertState();
    setHasHadGoodAudio(false);
  }, [resetAlertState]);

  useEffect(() => {
    if (!isWatching) reset();
  }, [isWatching, reset]);

  useEffect(() => {
    if (!isWatching) return;

    const isErrorStatus =
      currentAudioLevel === 'SILENT' || currentAudioLevel === 'VERY_LOW';

    const isGoodAudio = currentAudioLevel === 'NORMAL' || currentAudioLevel === 'LOW';

    if (isGoodAudio) {
      if (!hasHadGoodAudio) {
        onGoodAudioSeen();
      }

      setHasHadGoodAudio(true);
      resetAlertState();
      return;
    }

    if (alertType && isErrorStatus) {
      updateAlertType(currentAudioLevel as AudioAlertType);
      return;
    }

    if (isErrorStatus && !alertType && !timeoutRef.current) {
      const threshold = hasHadGoodAudio
        ? finalConfig.silenceThresholdMs
        : finalConfig.initialSilenceThresholdMs;

      timeoutRef.current = window.setTimeout(() => {
        timeoutRef.current = null;

        if (
          isWatching &&
          (currentAudioLevel === 'SILENT' || currentAudioLevel === 'VERY_LOW')
        ) {
          updateAlertType(currentAudioLevel as AudioAlertType);
        }
      }, threshold);
    }
  }, [
    currentAudioLevel,
    isWatching,
    alertType,
    hasHadGoodAudio,
    resetAlertState,
    updateAlertType,
    finalConfig,
    onGoodAudioSeen,
  ]);

  return { alertType, hasHadGoodAudio, reset };
};
